import { useCallback, useMemo } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { z } from "zod";
import { utils } from "xlsx";
import type { WorkBook } from "xlsx";

import { useOutcome } from "@pinatagrams/fiesta";

import {
	collectionItem,
	draftCollectionInput,
	type ApiCollection,
	type CollectionItem,
} from "./schema";
import {
	assumeValuesFromFileName,
	convertSpreadsheet,
	loadFile,
	type SpreadsheetConversionError,
} from "./editor/utils";

const collectionItemListSchema = z.array(collectionItem);

/**
 * Creates a Date object set to 3:00 AM Central Time for the given date.
 * Central Time is UTC-6 (CST) or UTC-5 (CDT).
 *
 * @param date - Optional date to use, defaults to today
 * @returns Date set to 3:00 AM Central Time
 */
function createFulfillAtDate(date?: Date): Date {
	const baseDate = date ? new Date(date) : new Date();
	// Create date string in format YYYY-MM-DD
	const dateStr = baseDate.toISOString().slice(0, 10);
	// Parse as 3:00 AM in Central Time (using en-US locale with Chicago timezone)
	// This will automatically handle CST/CDT
	const centralDate = new Date(`${dateStr}T03:00:00-06:00`);
	return centralDate;
}

export type CollectionFormValidation = {
	result: ReturnType<typeof draftCollectionInput.safeParse>;
	errors: string[];
};

export type UseCollectionFormResult = {
	data: CollectionItem[] | null;
	clientName: string;
	collectionName: string;
	fulfillAt: Date | null;
	fulfillBy: Date | null;
	workbook: WorkBook | null;
	sheets: string[];
	selectedSheet?: string;
	setSelectedSheet: (sheet: string) => void;
	setClientName: (name: string) => void;
	setCollectionName: (name: string) => void;
	setFulfillBy: (date: Date | string | null) => void;
	setFromCollection: (collection: ApiCollection) => void;
	setItemGraphic: CollectionFormState["setItemGraphic"];
	setItemMessage: CollectionFormState["setItemMessage"];
	reset: () => void;
	onInvalidFile: (files: File[] | number) => void;
	onFileDrop: (files: File[]) => void;
	validation: CollectionFormValidation;
};

export type CollectionFormState = {
	workbook: WorkBook | null;
	sheets: string[];
	selectedSheet?: string;
	data: CollectionItem[] | null;
	clientName: string;
	collectionName: string;
	fulfillAt: Date | null;
	fulfillBy: Date | null;
	setSelectedSheet: (sheet: string) => void;
	setWorkbook: (workbook: WorkBook | null) => void;
	setData: (data: CollectionItem[] | null) => void;
	setClientName: (name: string) => void;
	setCollectionName: (name: string) => void;
	setFulfillBy: (date: Date | string | null) => void;
	setFromCollection: (collection: ApiCollection) => void;
	setItemGraphic: (itemId: string, type: "outside" | "inside", url: string | null) => void;
	setItemMessage: (itemId: string, message: string | null) => void;
	resetForm: () => void;
	loadWorkbook: (workbook: WorkBook) => void;
	selectSheet: (sheet: string) =>
		| { success: true }
		| {
				success: false;
				error: Error;
				partialData?: CollectionItem[];
				errorDetails?: SpreadsheetConversionError[];
		  };
};

/**
 * Zustand store for collection form state.
 */
export const useCollectionFormStore = create<CollectionFormState>((set, get) => ({
	workbook: null,
	sheets: [],
	selectedSheet: undefined,
	data: null,
	clientName: "",
	collectionName: "",
	fulfillAt: createFulfillAtDate(), // Default to today at 3am Central
	fulfillBy: null,
	setSelectedSheet: (sheet) => {
		set({ selectedSheet: sheet });
	},
	setWorkbook: (workbook) => {
		set({ workbook });
	},
	setData: (data) => {
		set({ data });
	},
	setClientName: (name) => {
		set({ clientName: name });
	},
	setCollectionName: (name) => {
		set({ collectionName: name });
	},
	setFulfillBy: (date) => {
		set({ fulfillBy: typeof date === "string" ? new Date(date) : date });
	},
	setFromCollection: (collection) => {
		const client = "client" in collection ? collection.client || "" : "";
		const itemsResult =
			"items" in collection ? collectionItemListSchema.safeParse(collection.items) : null;
		const items = itemsResult && itemsResult.success ? itemsResult.data : null;
		set({
			clientName: client,
			collectionName: collection.name || "",
			fulfillAt: collection.fulfillAt || null,
			fulfillBy: collection.fulfillBy || null,
			data: items || null,
		});
	},
	setItemGraphic: (itemId, type, url) => {
		const currentData = get().data;
		if (!currentData) return;
		const updatedData = currentData.map((item) => {
			if (item.id !== itemId) return item;
			return {
				...item,
				graphics: {
					...item.graphics,
					[type]: url,
				},
			};
		});
		set({ data: updatedData });
	},
	setItemMessage: (itemId, message) => {
		const currentData = get().data;
		if (!currentData) return;
		const updatedData = currentData.map((item) => {
			if (item.id !== itemId) return item;
			return {
				...item,
				message,
			};
		});
		set({ data: updatedData });
	},
	resetForm: () => {
		set({
			workbook: null,
			sheets: [],
			selectedSheet: undefined,
			data: null,
			clientName: "",
			collectionName: "",
			fulfillAt: createFulfillAtDate(), // Reset to today at 3am Central
			fulfillBy: null,
		});
	},
	loadWorkbook: (workbook) => {
		const sheets = workbook.SheetNames;
		const selectedSheet = sheets[0];
		set({ workbook, sheets, selectedSheet });
		if (selectedSheet) {
			get().selectSheet(selectedSheet);
		}
	},
	selectSheet: (sheet) => {
		const workbook = get().workbook;
		if (!workbook) {
			return { success: false, error: new Error("No workbook loaded") };
		}
		try {
			const worksheet = workbook.Sheets[sheet];
			if (!worksheet) {
				return { success: false, error: new Error(`Sheet not found: ${sheet}`) };
			}
			const sheetData = utils.sheet_to_json<Record<string, unknown>>(worksheet, {
				defval: "",
				raw: false,
			});
			const conversionResult = convertSpreadsheet(sheetData);

			// If there are conversion errors, include them in the result
			if (conversionResult.hasErrors) {
				const errorMessages = conversionResult.errors
					.slice(0, 5) // Show first 5 errors
					.map((err) => `Row ${err.rowNumber}: ${err.message}`)
					.join("\n");
				const totalErrorCount = conversionResult.errors.length;
				const errorSummary =
					totalErrorCount > 5
						? `${errorMessages}\n... and ${totalErrorCount - 5} more errors`
						: errorMessages;

				return {
					success: false,
					error: new Error(`Spreadsheet conversion errors:\n${errorSummary}`),
					partialData: conversionResult.items,
					errorDetails: conversionResult.errors,
				};
			}

			const parsed = collectionItemListSchema.safeParse(conversionResult.items);
			if (!parsed.success) {
				const firstIssue = parsed.error.issues[0]?.message ?? "Invalid spreadsheet data";
				return { success: false, error: new Error(firstIssue) };
			}
			set({ selectedSheet: sheet, data: parsed.data });
			return { success: true };
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			return { success: false, error: err };
		}
	},
}));

/**
 * Collection form hook backed by the zustand store.
 *
 * @param originId - Outcome origin id for form messages
 * @returns Form state and actions
 */
export const useCollectionForm = (originId: string): UseCollectionFormResult => {
	const {
		data,
		clientName,
		collectionName,
		fulfillAt,
		fulfillBy,
		workbook,
		sheets,
		selectedSheet,
		setClientName,
		setCollectionName,
		setFulfillBy,
		setFromCollection,
		setItemGraphic,
		resetForm,
		loadWorkbook,
		selectSheet,
	} = useCollectionFormStore(
		useShallow((state) => ({
			data: state.data,
			clientName: state.clientName,
			collectionName: state.collectionName,
			fulfillAt: state.fulfillAt,
			fulfillBy: state.fulfillBy,
			workbook: state.workbook,
			sheets: state.sheets,
			selectedSheet: state.selectedSheet,
			setClientName: state.setClientName,
			setCollectionName: state.setCollectionName,
			setFulfillBy: state.setFulfillBy,
			setFromCollection: state.setFromCollection,
			setItemGraphic: state.setItemGraphic,
			resetForm: state.resetForm,
			loadWorkbook: state.loadWorkbook,
			selectSheet: state.selectSheet,
		})),
	);
	const { emit, clearOrigin } = useOutcome();
	const setItemMessage = useCallback<CollectionFormState["setItemMessage"]>((itemId, message) => {
		useCollectionFormStore.getState().setItemMessage(itemId, message);
	}, []);

	const onInvalidFile = useCallback(
		(files: File[] | number) => {
			const params = {
				origin: { id: originId },
				impact: "blocking" as const,
				severity: "fatal" as const,
				intent: "inform" as const,
				actions: [],
			};
			if (typeof files === "number") {
				emit({
					...params,
					title: "Too many files",
					message: `You can only upload up to 1 file at once. You tried to upload ${files}.`,
				});
				return;
			}
			emit({
				...params,
				title: "Invalid file type",
				message: `The following files are not accepted: ${files.map((f) => f.name).join(", ")}. Please upload files with the following extensions: ${[".csv", ".xlsx", ".xls"].join(", ")}.`,
			});
		},
		[emit, originId],
	);

	const onFileDrop = useCallback(
		(files: File[]) => {
			const file = files[0];
			if (!file) return;
			const assumedValues = assumeValuesFromFileName(file.name);
			if (assumedValues.clientName) setClientName(assumedValues.clientName);
			if (assumedValues.collectionName) setCollectionName(assumedValues.collectionName);
			loadFile(file, (wb) => {
				loadWorkbook(wb);
			});
		},
		[loadWorkbook, setClientName, setCollectionName],
	);

	const validation = useMemo<CollectionFormValidation>(() => {
		const result = draftCollectionInput.safeParse({
			client: clientName,
			name: collectionName,
			fulfillAt: fulfillAt || null,
			fulfillBy: fulfillBy || null,
			manifest: { image: null },
		});
		const errors: string[] = [];
		if (!result.success) {
			result.error.issues.forEach((issue) => {
				errors.push(issue.message);
			});
		}
		return { result, errors };
	}, [clientName, collectionName, fulfillAt, fulfillBy]);

	const reset = useCallback(() => {
		resetForm();
		clearOrigin(originId);
	}, [clearOrigin, originId, resetForm]);

	const handleSelectedSheetChange = useCallback(
		(sheet: string) => {
			const selection = selectSheet(sheet);
			if (!selection.success) {
				emit({
					origin: { id: originId },
					impact: "blocking",
					success: false,
					title: "Issues found in spreadsheet",
					message: selection.error.message,
				});

				// Still load partial data if available
				if (
					"partialData" in selection &&
					selection.partialData &&
					selection.partialData.length > 0
				) {
					const parsed = collectionItemListSchema.safeParse(selection.partialData);
					if (parsed.success) {
						useCollectionFormStore.getState().setData(parsed.data);
						// Emit info notification about valid rows loaded
						emit({
							origin: { id: originId },
							impact: "blocking",
							success: false,
							title: "Partial data loaded",
							message: `${parsed.data.length} rows were successfully parsed and loaded. Please review the errors above to fix the remaining invalid rows.`,
						});
					}
				}
				return;
			}
		},
		[emit, originId, selectSheet],
	);
	return {
		data,
		clientName,
		collectionName,
		fulfillAt,
		fulfillBy,
		workbook,
		sheets,
		selectedSheet,
		setSelectedSheet: handleSelectedSheetChange,
		setClientName,
		setCollectionName,
		setFulfillBy,
		setFromCollection,
		setItemGraphic,
		setItemMessage,
		reset,
		onInvalidFile,
		onFileDrop,
		validation,
	};
};
